import {
  Fragment,
  FunctionComponent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { FixedSizeGrid, GridChildComponentProps, GridOnItemsRenderedProps } from 'react-window';
import cn from 'classnames';
import { Appearance, Design, HotTopic, ProductType, Shop } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isCYOMode } from '@/utils/params';
import { useDimension } from '@/hooks/useDimension';
import { useRememberFixedSizeGridPosition } from '@/hooks/useRememberGridPosition';
import { useScrollbarWidth } from '@/hooks/useScrollbarWidth';
import { getIsSingleConfigurationAppearance } from '@/model/productType/getIsSingleConfigurationAppearance';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import { StickerHint } from '@/components/common/StickerHint';
import { Search } from '@/components/common/icons/Search';
import { DesignItem } from './DesignItem';
import { DesignSelectorTab, LIST_ROUTE, addSearchTermToRoute } from './DesignSelector';
import './DesignList.scss';

const StartASearch = () => (
  <div className="start-a-search">
    <img
      className="start-a-search__arrow"
      src="https://assets.spreadshirt.net/cyo/images/arrow.png"
      width="142"
      height="113"
      alt=""
    />
    <div className="start-a-search__image">
      <img
        src="https://assets.spreadshirt.net/cyo/images/imagestack.png"
        width="137"
        height="119"
        alt=""
      />
      <h2>{t('designPanel.startBadge')}</h2>
    </div>
  </div>
);

const NoResult = () => (
  <div className="no-results">
    <Search width="2em" height="2em" className="no-results__icon" />
    <span>{t('designPanel.noSearchResults')}</span>
  </div>
);

type NoResultCYOProps = {
  topics: HotTopic[];
};

const NoResultCYO: FunctionComponent<NoResultCYOProps> = ({ topics }) => (
  <div className="no-results-cyo">
    <h3 className="no-results-cyo__headline">{t('designPanel.noResultMessage.headline')}</h3>
    <ul className="no-results-cyo__list">
      <li>{t('designPanel.noResultMessage.change')}</li>
      <li>
        {t('designPanel.noResultMessage.tryHotTopic')}{' '}
        {topics?.slice(0, 3).map((topic, index) => (
          <Fragment key={topic.designId}>
            {index > 0 && ' | '}
            <Link
              to={addSearchTermToRoute(LIST_ROUTE, topic.value)}
              className="no-results-cyo__link-topic"
              onClick={() =>
                trackFeature('design-selection-hot-topic', { term: topic.value }, true)
              }
            >
              {topic.value}
            </Link>
          </Fragment>
        ))}
      </li>
      <li>
        {`${t('designPanel.noResultMessage.contact')} ${t('contact.callUs')} `}
        <a className="no-results-cyo__link-contact" href={t('contact.phoneHref')}>
          {t('contact.phone')}
        </a>
        {` | ${t('contact.emailUs')} `}
        <a className="no-results-cyo__link-contact" href={t('contact.emailHref')}>
          {t('contact.email')}
        </a>
        {` | ${t('contact.whatsAppUs')} `}
        <a className="no-results-cyo__link-contact" href="https://wa.me/004934125049804">
          {t('contact.whatsAppLink')}
        </a>
      </li>
    </ul>
  </div>
);

interface CellData
  extends Pick<
    Props,
    | 'designs'
    | 'productType'
    | 'printabilityMap'
    | 'context'
    | 'selectedTab'
    | 'selectedDesignId'
    | 'favoritedDesignIds'
    | 'showDesignPrices'
    | 'selectDesign'
    | 'toggleDesignFavorite'
  > {
  columnCount: number;
  imageHeight: number;
  isSingle: boolean;
}

interface CellProps extends GridChildComponentProps {
  data: CellData;
}

const Cell: FunctionComponent<CellProps> = ({
  columnIndex,
  rowIndex,
  style,
  data: {
    columnCount,
    designs,
    printabilityMap,
    imageHeight,
    productType,
    favoritedDesignIds,
    selectedDesignId,
    showDesignPrices,
    toggleDesignFavorite,
    context,
    selectedTab,
    selectDesign,
    isSingle,
  },
}) => {
  let itemIndex = columnCount * rowIndex + columnIndex;

  if (isSingle) {
    if (itemIndex === 0) {
      return (
        <div style={style}>
          <StickerHint height={imageHeight + 16} />
        </div>
      );
    }

    itemIndex--;
  }

  const design = designs[itemIndex];
  if (!design) {
    return null;
  }

  const designId = design.id;
  const isPrintable = printabilityMap.get(designId);
  const tileClasses = cn('design-item', { 'design-item--not-printable': !isPrintable });
  const isFavorited = favoritedDesignIds.includes(designId);

  return (
    <div style={style} className={tileClasses}>
      <DesignItem
        onClick={selectDesign}
        design={design}
        imageHeight={imageHeight}
        isPrintable={isPrintable}
        isFavorited={isFavorited}
        isSelected={designId === selectedDesignId}
        showDesignPrices={showDesignPrices}
        toggleDesignFavorite={toggleDesignFavorite}
        context={context}
        isMarketplaceCategoryActive={selectedTab === DesignSelectorTab.Community}
        productType={productType}
      />
    </div>
  );
};

type Props = {
  loading: boolean;
  isSmall: boolean;
  designs: Design[];
  designCount: number;
  limit: number;
  selectedTab: DesignSelectorTab;
  productType: ProductType;
  appearance: Appearance;
  printabilityMap: Map<string, boolean>;
  scrollKey: number;
  scrollStartIndex: MutableRefObject<number>;
  context: Shop;
  selectedDesignId?: string;
  favoritedDesignIds: string[];
  showDesignPrices?: boolean;
  selectDesign: (design: Design) => void;
  toggleDesignFavorite: (design: Design, isFavorited: boolean) => void;
  topics: HotTopic[];
  loadMoreItems: (startIndex: number) => Promise<void>;
  filterUnprintableDesigns: boolean;
  isBlocklisted: boolean;
};
export const DesignList: FunctionComponent<Props> = ({
  loading,
  isSmall,
  designs,
  designCount,
  limit,
  selectedTab,
  productType,
  appearance,
  printabilityMap,
  scrollKey,
  scrollStartIndex,
  context,
  selectedDesignId,
  favoritedDesignIds,
  showDesignPrices,
  selectDesign,
  toggleDesignFavorite,
  topics,
  loadMoreItems,
  filterUnprintableDesigns,
  isBlocklisted,
}) => {
  const isSingle = getIsSingleConfigurationAppearance(appearance);
  const itemWidth = isSmall ? 140 : 200;
  const imageHeight = isSmall ? 80 : 150;

  const visibleDesigns = useMemo(() => {
    if (filterUnprintableDesigns) {
      return designs.filter(({ id }) => printabilityMap.get(id));
    }

    return designs;
    // printabilityMap is only a new map if the product type or appearance changes
    // so we need to recalculate this if new elements are added to the map too
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUnprintableDesigns, designs, printabilityMap, printabilityMap.size]);

  const gridItemCount = visibleDesigns.length + (isSingle ? 1 : 0);
  const [measureRef, dimension] = useDimension();
  const [measureGridRef, gridDimension] = useDimension();
  const [scrollbarWidth, scrollContainerRef] = useScrollbarWidth([dimension, gridDimension]);
  const gridWidth = dimension.width - scrollbarWidth;
  const columnCount = Math.floor(gridWidth / itemWidth) || 1;
  const columnWidth = Math.floor(gridWidth / columnCount);
  const rowCount = Math.ceil(gridItemCount / columnCount);

  const [gridRef, onItemsRenderedGridPosition] = useRememberFixedSizeGridPosition(
    scrollStartIndex,
    columnCount
  );

  const lastLoadedIndex = useRef(0);
  const lastScrolledIndex = useRef(0);
  useEffect(() => {
    lastLoadedIndex.current = lastScrolledIndex.current = 0;
  }, [loadMoreItems]);

  const loadMoreItemsIfNecessary = useCallback(() => {
    const loadedDesignCount = designs.length;
    if (
      loadedDesignCount < designCount && // more designs are available
      loadedDesignCount !== lastLoadedIndex.current && // not already loading the next batch
      visibleDesigns.length - lastScrolledIndex.current < limit / 2 // fewer than half a batch size items are ahead
    ) {
      lastLoadedIndex.current = loadedDesignCount;
      loadMoreItems(loadedDesignCount); // can/should we do anything if the batch request failed?
    }
  }, [designs, designCount, visibleDesigns, limit, loadMoreItems]);

  useEffect(loadMoreItemsIfNecessary, [loadMoreItemsIfNecessary]);

  function onItemsRendered(props: GridOnItemsRenderedProps) {
    onItemsRenderedGridPosition(props);

    lastScrolledIndex.current = (props.overscanRowStopIndex + 1) * columnCount; // index of last visible item in overscanRow

    loadMoreItemsIfNecessary();
  }

  let content;
  if (selectedTab === DesignSelectorTab.Community && designCount === -1) {
    content = <StartASearch />;
  } else if (designCount === 0 || isBlocklisted) {
    content = isCYOMode() && topics.length > 0 ? <NoResultCYO topics={topics} /> : <NoResult />;
  } else {
    const itemData: CellData = {
      designs: visibleDesigns,
      productType,
      printabilityMap,
      context,
      selectedTab,
      selectedDesignId,
      favoritedDesignIds,
      showDesignPrices,
      selectDesign,
      toggleDesignFavorite,
      columnCount,
      imageHeight,
      isSingle,
    };

    content = (
      <FixedSizeGrid
        key={scrollKey}
        ref={gridRef}
        outerRef={(element) => {
          measureGridRef(element);
          scrollContainerRef.current = element;
        }}
        className="design-list__grid"
        width={dimension.width}
        height={dimension.height}
        columnCount={columnCount}
        columnWidth={columnWidth}
        rowCount={rowCount}
        rowHeight={
          imageHeight +
          55 /* 8px (margin-top) + 16px (margin image) + 2px (border image) + 29px (height info) */
        }
        onItemsRendered={onItemsRendered}
        itemData={itemData}
      >
        {Cell}
      </FixedSizeGrid>
    );
  }

  return (
    <div className="design-list">
      <div className="design-list__content" ref={measureRef}>
        {content}
      </div>
      {loading && <LoadingOverlay />}
    </div>
  );
};
